import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { PlacesAutocomplete } from './places-search'
import { VirtualPlacesAutocomplete } from './virtual-places-search'
import { escapeHtml, unescapeHtml } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-places-search-field', {
  title: 'Lieu*',
  placeholder: 'Entrez le nom ou l’adresse du lieu',
  choose: 'Choisir',
  addLocation: 'Ajouter un lieu',
})

Locale.getInstance().registerKeySet('en', 'lvc-places-search-field', {
  title: 'Location*',
  placeholder: 'Enter place name or address',
  choose: 'Choose',
  addLocation: 'Add a location',
})

export interface LvcLocationSearchField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
  virtual: boolean
}

export class LvcLocationSearchField extends MinzeElement {
  private places: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = [
    'title',
    'placeholder',
    'error',
    ['default-value', ''],
    ['on-change', ''],
    'virtual',
  ]

  reactive: Reactive = [
    ['value', ''],
    ['tooltipOpened', false],
  ]

  tooltipElement: HTMLElement | null = null

  static observedAttributes = ['title', 'on-change', 'error', 'virtual']

  autocompleteCharactersThreshold = 3

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  async onReady() {
    this.tooltipElement = this.select('#places-tooltip')

    if (this.autocompleteCharactersThreshold > 0) {
      const autocompleteInstance = this.virtual
        ? VirtualPlacesAutocomplete.instance
        : PlacesAutocomplete.instance
      const list = (await autocompleteInstance.getSuggestions(this.filter)) as any[]
      this.places = list || []
    }

    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedIds = JSON.parse(decodeURIComponent(this.defaultValue)).map((k: any) => k.id)
    }

    this.rerender()
  }

  async onAttributeChange(name?: string, oldValue?: string | null, newValue?: string | null) {
    if (this.autocompleteCharactersThreshold > 0) {
      if (name === 'virtual') {
        const autocompleteInstance = this.virtual
          ? VirtualPlacesAutocomplete.instance
          : PlacesAutocomplete.instance
        const list = (await autocompleteInstance.getSuggestions(this.filter)) as any[]
        this.places = list || []
      }
    }
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  getSelectedValuesLabels() {
    return this.places
      .filter(k => this.selectedIds.indexOf(`${k.id}`) > -1)
      .map(k => (Locale.language === 'en' ? k.nameEn : k.nameFr))
  }

  onPlaceClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')
    const place = this.places.find(place => place.id === value || place.id.toString() === value)

    if (!value) {
      return
    }
    let index = this.selectedIds.indexOf(value)

    if (index > -1) {
      this.selectedIds.splice(index, 1)
    } else {
      this.selectedIds = [value]
    }

    const labels = this.getSelectedValuesLabels()
    Callback.call(this.onChange, {
      value: labels?.[0] || '',
      place: place || null,
      element: this.shadowRoot?.host,
    })

    if (labels?.[0]) this.onToggle()

    this.rerender(true)
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    if (this.filter.length >= this.autocompleteCharactersThreshold) {
      const autocompleteInstance = this.virtual
        ? VirtualPlacesAutocomplete.instance
        : PlacesAutocomplete.instance
      const list = (await autocompleteInstance.getSuggestions(this.filter)) as any[]
      this.places = list || []
    } else {
      this.places = []
    }
    this.rerender()
  }

  addLocation() {
    Callback.call(this.onChange, {
      value: this.filter,
      element: this.shadowRoot?.host,
    })
    this.onToggle()
  }

  serializeValue() {
    let objects = this.places.filter(k => this.selectedIds.indexOf(k.id) > -1)
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('change', objects)
  }

  getPlacesLabel() {
    if (this.selectedIds.length === 0) {
      return `
        <span>
          ${
            this.defaultValue && this.defaultValue !== true
              ? this.defaultValue
              : Locale.getKey('lvc-places-search-field.placeholder')
          }
        </span>
      `
    }

    let labels = this.getSelectedValuesLabels()

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderPlace = (place: any) => {
    let isNotSelected = this.selectedIds.indexOf(`${place.id}`) == -1

    return `
      <button 
        name="place"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${place.id}">
        ${Locale.language === 'en' ? place.nameEn : place.nameFr}
        ${isNotSelected ? '' : '<lvc-circle-check-select-icon></lvc-circle-check-select-icon>'}
      </button>
    `
  }

  renderPlaces = () => {
    let html = ''

    for (let i = 0; i < this.places.length; i++) {
      html += this.renderPlace(this.places[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0
    return `
      <div class="title">${this.title || Locale.getKey('lvc-places-search-field.title')}
      </div>
      <div class="input ${this.selectedIds?.length || (this.defaultValue && this.defaultValue !== true) ? 'has-value' : ''}  ${this.showError ? 'error' : ''}">
        <button class="dropbtn" name="toggle">
          <lvc-search-icon></lvc-search-icon>
          ${this.getPlacesLabel()}
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
          <lvc-search-icon></lvc-search-icon>
            <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-places-search-field.placeholder')}"/>
          </div>
          <div class="places" data-selected="${this.selectedIds.length}">
            ${this.filter.length >= this.autocompleteCharactersThreshold ? this.renderPlaces() : ''}
          </div> 
          <div class="footer">
            <button class="addLocation">
              <lvc-add-icon></lvc-add-icon>
              ${Locale.getKey('lvc-places-search-field.addLocation')}
            </button>
          </div>
        </div> 
      </div>
      ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
    `
  }

  css = () => {
    return `
      :host {
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .footer {
        display: flex;
        height: 60px;
        padding: 0 10px;
      }

      .tooltip-dialog {
        display: none;
        position: absolute;
        background-color: #fff;
        border: 1px solid #ccc;
        padding: 10px;
        z-index: 3; /* Ensure the dialog appears above other elements */
        /* Add any other styles for the dialog */
    }

      lvc-add-icon {
        display: flex;
        align-items: center;
      }

      .addLocation {
        color: #ffff;
        background: var(--Minuit, #1E007D);
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%; 
        display: flex;
        padding: 10px 16px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex: 1 1 0;
        border: none;
        margin: 10px;
        cursor: pointer;
        border-radius: 999px;
      }

      .header {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 8px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: #BFF0FF;

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
        display: flex;
        

        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 3.5px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 60px;
      }
      
      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }

      .places {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .places button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }



      .places button:last-child {
        border-bottom: none;
      }

      .places button.selected {
      }

      .places-tooltip-content ul {
        margin: 0;
        padding-left: 15px;
        list-style: none; /* Remove default list style */
      }
  
      .places-tooltip-content li {
        font-size: 14px;
        font-weight: 500;
        line-height: 21px;
        letter-spacing: 0px;
        text-align: left;
        
      }
      
      .places-tooltip-content li::before {
        content: ''; /* Empty content to remove default bullet point */
        display: inline-block;
        width: 9px;
        background-color: red;
        height: 10px;
        margin-right: 8px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10" fill="none" stroke="%231E007D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 9C6.72234 7.77224 5.49206 6.47963 4.31317 5.1268M4.31317 5.1268C3.16768 3.81229 2.04474 2.42401 1 1M4.31317 5.1268C5.39654 3.6513 6.68118 2.41964 8 1M4.31317 5.1268C3.06821 6.63983 2.15598 7.52691 1 9" /></svg>') no-repeat center center;
      }

      .places-tooltip-content h3 {
        margin: 0 0 8px;
      }
      
      .places-tooltip-content ul {
        margin: 0;
        padding-left: 15px;
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 12px 8px;
        font-size: 14px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }
      
      .dropbtn span {
        color: rgba(30, 0, 125, 0.5);
      }
      
      .input.has-value .dropbtn span {
        color: rgba(30, 0, 125, 1);
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      lvc-search-icon {
        margin-right: 8px;
        display:flex;
        align-items: center;
      }

      lvc-help-icon {
        display:flex;
        align-items: center;
        margin-left: 4px;
        cursor: pointer;
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #ffff;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }

      .close-overlay {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1;
      }

      .places::-webkit-scrollbar {
        display: none;
      }

      .error {
        background: #FFEBD7;
      }
    `
  }

  eventListeners: EventListeners = [
    ['button[name="place"]', 'click', this.onPlaceClick.bind(this)],
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
    ['.addLocation', 'click', this.addLocation.bind(this)],
  ]
}
